import { Component, OnInit, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog } from '@angular/material/dialog';
import { AssetMappingConfig, getFieldConfig, setFieldConfig, buildFieldConfigKey } from '../../../core/models/asset-config.model';
import { CatalogAttributeV2, MergedField } from '../../../core/models/catalog.model';
import { CatalogService } from '../../../core/services/catalog.service';
import { UtilsService } from '../../../core/services/utils.service';
import { WarningModalComponent } from '../warning-modal/warning-modal.component';
import {
  faSearch, faTimes as faClear, faWarning, faInfoCircle, faGripVertical,
  faSignal, faCogs, faDesktop, faBuilding, faWrench, faQuestionCircle,
  faToggleOn, faComment, faColumns, faListUl, faSort,
  faChevronRight, faChevronDown, faChevronUp, faAngleDoubleUp, faAngleDoubleDown, faStar, faTrash, faBan
} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-map-configuration20',
  templateUrl: './map-configuration20.component.html',
  styleUrls: ['./map-configuration20.component.scss']
})
export class MapConfiguration20Component implements OnInit, OnDestroy, OnChanges {
  private static readonly MAX_POPUP_FIELDS = 10;
  private destroy$ = new Subject<void>();

  @Input() catalogItems: CatalogAttributeV2[] = [];
  @Input() mappingConfig: AssetMappingConfig | null = null;
  @Input() selectedAudience: 'USER' | 'CUSTOMER' | 'GLOBAL' = 'USER';

  faSearch = faSearch;
  faClear = faClear;
  faWarning = faWarning;
  faInfoCircle = faInfoCircle;
  faGripVertical = faGripVertical;
  faSignal = faSignal;
  faCogs = faCogs;
  faDesktop = faDesktop;
  faBuilding = faBuilding;
  faWrench = faWrench;
  faQuestionCircle = faQuestionCircle;
  faToggleOn = faToggleOn;
  faComment = faComment;
  faColumns = faColumns;
  faListUl = faListUl;
  faSort = faSort;
  faChevronRight = faChevronRight;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;
  faAngleDoubleUp = faAngleDoubleUp;
  faAngleDoubleDown = faAngleDoubleDown;
  faStar = faStar;
  faTrash = faTrash;
  faBan = faBan;

  mapFields: { [scope: string]: MergedField[] } = {};
  filteredMapFields: { [scope: string]: MergedField[] } = {};
  normalFieldsByScope: { [scope: string]: MergedField[] } = {};
  deletedFieldsByScope: { [scope: string]: MergedField[] } = {};
  disabledFieldsByScope: { [scope: string]: MergedField[] } = {};

  isLoadingConfig = false;
  configError: string | null = null;
  expandedGroups: { [scope: string]: boolean } = {};
  searchQuery = '';
  selectedModel = '';
  availableModels: string[] = [];
  scopeOrder: string[] = [];
  stylesheetString: string = 'aft';

  constructor(
    private translateService: TranslateService,
    private dialog: MatDialog,
    private catalogService: CatalogService,
    private utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    this.stylesheetString = this.utilsService.getStylesheetString();
    this.buildMergedFields();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['catalogItems'] || changes['mappingConfig']) {
      this.dirtyFieldKeys.clear();
      this.buildMergedFields();
    }
  }

  buildMergedFields(): void {
    this.mapFields = this.catalogService.buildMapConfigFields(this.catalogItems, this.mappingConfig);

    if (!Object.keys(this.mapFields).length) {
      this.filteredMapFields = {};
      this.scopeOrder = [];
      return;
    }

    const modelsSet = new Set<string>();
    Object.values(this.mapFields).forEach(fields =>
      fields.forEach(f => f.availableInModels?.forEach(m => modelsSet.add(m)))
    );

    this.availableModels = Array.from(modelsSet).sort();
    this.loadScopeOrder();
    this.applyFilters();
  }

  /**
   * Crea l'entry in mappingConfig.fields se non esiste ancora (campo "new" del catalog).
   * La entry è indicizzata per scope+name (vedi asset-config.model.ts), così due catalog item con
   * lo stesso name ma scope diverso non condividono la stessa configurazione.
   */
  private ensureFieldInMapping(fieldKey: string, scope: string): void {
    if (!this.mappingConfig) return;
    if (getFieldConfig(this.mappingConfig, scope, fieldKey)) return;

    const catalogItem = this.catalogItems.find(c => c.name === fieldKey && c.scope === scope);
    setFieldConfig(this.mappingConfig, scope, fieldKey, {
      type: catalogItem?.type ?? 'unknown',
      scope,
      label: catalogItem?.label ?? fieldKey,
      unit: catalogItem?.unit ?? undefined,
      visible: true,
      displayOrder: Object.keys(this.mappingConfig.fields ?? {}).length
    });
  }

  private markFieldKnown(fieldKey: string, scope: string): void {
    for (const fields of [this.mapFields, this.filteredMapFields]) {
      const f = fields[scope]?.find(x => x.key === fieldKey);
      if (f) f.isNew = false;
    }
  }

  applyFilters(): void {
    const query = this.searchQuery.toLowerCase().trim();
    const modelFilter = this.selectedModel;
    this.filteredMapFields = {};
    this.normalFieldsByScope = {};
    this.deletedFieldsByScope = {};
    this.disabledFieldsByScope = {};

    Object.entries(this.mapFields).forEach(([scope, fields]) => {
      const filtered = fields
        .filter(f => {
          const matchesSearch = !query || f.key.toLowerCase().includes(query) || f.label.toLowerCase().includes(query);
          const matchesModel = !modelFilter || f.availableInModels?.includes(modelFilter);
          return matchesSearch && matchesModel;
        })
        .map(f => ({
          ...f,
          highlighted: query.length > 0 && (f.key.toLowerCase().includes(query) || f.label.toLowerCase().includes(query))
        }));

      if (filtered.length > 0) {
        this.filteredMapFields[scope] = filtered;
        this.normalFieldsByScope[scope] = filtered.filter(f => !f.deletedFromCatalog && !f.disabledInCatalog);
        this.deletedFieldsByScope[scope] = filtered.filter(f => f.deletedFromCatalog);
        this.disabledFieldsByScope[scope] = filtered.filter(f => f.disabledInCatalog && !f.deletedFromCatalog);
      }
    });
  }

  onSearchChange(_v: string): void { this.applyFilters(); }
  clearSearch(): void { this.searchQuery = ''; this.applyFilters(); }
  onModelFilterChange(_v: string): void { this.applyFilters(); }

  private dirtyFieldKeys = new Set<string>();

  toggleFieldVisibility(fieldKey: string, scope: string): void {
    this.ensureFieldInMapping(fieldKey, scope);
    const config = getFieldConfig(this.mappingConfig, scope, fieldKey)!;
    config.visible = config.visible === false;
    this.dirtyFieldKeys.add(this.dirtyKey(fieldKey, scope));
    this.buildMergedFields();
  }

  toggleFieldDestination(fieldKey: string, scope: string, destination: 'popup' | 'sidebar' | 'details', checked: boolean): void {
    this.ensureFieldInMapping(fieldKey, scope);
    const config = getFieldConfig(this.mappingConfig, scope, fieldKey)!;
    switch (destination) {
      case 'popup':   config.showInPopup = checked; break;
      case 'sidebar': config.showInSidebar = checked; break;
      case 'details': config.showInDetails = checked; break;
    }
    this.dirtyFieldKeys.add(this.dirtyKey(fieldKey, scope));
    this.markFieldKnown(fieldKey, scope);
  }

  getFieldDestination(fieldKey: string, scope: string, destination: 'popup' | 'sidebar' | 'details'): boolean {
    const config = getFieldConfig(this.mappingConfig, scope, fieldKey);
    if (!config) {
      return false;
    }
    switch (destination) {
      case 'popup':   return config.showInPopup === true;
      case 'sidebar': return config.showInSidebar === true;
      case 'details': return config.showInDetails === true;
      default:        return false;
    }
  }

  onPopupCheckboxClick(event: MouseEvent, fieldKey: string, scope: string): void {
    event.preventDefault();
    event.stopPropagation();
    const current = this.getFieldDestination(fieldKey, scope, 'popup');
    const count = this.getPopupFieldsCount();
    if (!current && count >= MapConfiguration20Component.MAX_POPUP_FIELDS) {
      this.dialog.open(WarningModalComponent, {
        width: '400px',
        data: this.translateService.instant('confSignalsPanel.popupLimitReachedMessage', { field: fieldKey, max: MapConfiguration20Component.MAX_POPUP_FIELDS })
      });
      return;
    }
    this.toggleFieldDestination(fieldKey, scope, 'popup', !current);
  }

  private getPopupFieldsCount(): number {
    return ([] as MergedField[]).concat(...Object.values(this.mapFields))
      .filter((f: MergedField) => f.visible && this.getFieldDestination(f.key, f.scope, 'popup')).length;
  }

  toggleAllSidebar(scope: string, checked: boolean): void {
    this.normalFieldsByScope[scope]?.filter(f => f.visible)
      .forEach(f => { this.toggleFieldDestination(f.key, f.scope, 'sidebar', checked); });
  }

  toggleAllDetails(scope: string, checked: boolean): void {
    this.normalFieldsByScope[scope]?.filter(f => f.visible)
      .forEach(f => { this.toggleFieldDestination(f.key, f.scope, 'details', checked); });
  }

  areAllSidebarChecked(scope: string): boolean {
    const visible = this.normalFieldsByScope[scope]?.filter(f => f.visible) ?? [];
    return visible.length > 0 && visible.every(f => this.getFieldDestination(f.key, f.scope, 'sidebar'));
  }

  areAllDetailsChecked(scope: string): boolean {
    const visible = this.normalFieldsByScope[scope]?.filter(f => f.visible) ?? [];
    return visible.length > 0 && visible.every(f => this.getFieldDestination(f.key, f.scope, 'details'));
  }

  isSidebarIndeterminate(scope: string): boolean {
    const visible = this.normalFieldsByScope[scope]?.filter(f => f.visible) ?? [];
    const n = visible.filter(f => this.getFieldDestination(f.key, f.scope, 'sidebar')).length;
    return n > 0 && n < visible.length;
  }

  isDetailsIndeterminate(scope: string): boolean {
    const visible = this.normalFieldsByScope[scope]?.filter(f => f.visible) ?? [];
    const n = visible.filter(f => this.getFieldDestination(f.key, f.scope, 'details')).length;
    return n > 0 && n < visible.length;
  }

  onScopeToggleChange(scope: string): void {
    const normal = this.normalFieldsByScope[scope];
    if (!normal?.length) return;
    const allEnabled = normal.every(f => f.visible);
    normal.forEach(f => {
      this.ensureFieldInMapping(f.key, f.scope);
      getFieldConfig(this.mappingConfig, f.scope, f.key)!.visible = !allEnabled;
      this.dirtyFieldKeys.add(this.dirtyKey(f.key, f.scope));
    });
    this.buildMergedFields();
  }

  isScopeEnabled(scope: string): boolean {
    const normal = this.normalFieldsByScope[scope] ?? [];
    return normal.length > 0 && normal.every(f => f.visible);
  }

  isScopeIndeterminate(scope: string): boolean {
    const normal = this.normalFieldsByScope[scope] ?? [];
    const v = normal.filter(f => f.visible).length;
    return v > 0 && v < normal.length;
  }

  onScopeDrop(event: CdkDragDrop<string[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.scopeOrder, event.previousIndex, event.currentIndex);
      this.saveScopeOrder();
    }
  }

  onFieldDrop(event: CdkDragDrop<MergedField[]>, scope: string): void {
    this.reorderField(scope, event.previousIndex, event.currentIndex);
  }

  /** Sposta un campo di una posizione in su (direction -1) o in giù (direction +1), alternativa al drag & drop */
  moveField(scope: string, index: number, direction: -1 | 1): void {
    this.moveFieldTo(scope, index, index + direction);
  }

  /** Sposta un campo a una posizione precisa (es. primo o ultimo), utile con liste lunghe */
  moveFieldTo(scope: string, index: number, target: number): void {
    const normalFields = this.normalFieldsByScope[scope] ?? [];
    if (target < 0 || target >= normalFields.length) return;
    this.reorderField(scope, index, target);
  }

  private reorderField(scope: string, fromIndex: number, toIndex: number): void {
    if (fromIndex === toIndex || !this.mappingConfig) return;
    const normalFields = this.normalFieldsByScope[scope] ?? [];
    moveItemInArray(normalFields, fromIndex, toIndex);
    normalFields.forEach((field, index) => {
      this.ensureFieldInMapping(field.key, field.scope);
      getFieldConfig(this.mappingConfig, field.scope, field.key)!.displayOrder = index;
      field.isNew = false;
      this.dirtyFieldKeys.add(this.dirtyKey(field.key, field.scope));
    });
    this.filteredMapFields[scope] = [...normalFields, ...(this.deletedFieldsByScope[scope] ?? []), ...(this.disabledFieldsByScope[scope] ?? [])];
  }

  private dirtyKey(fieldKey: string, scope: string): string {
    return buildFieldConfigKey(scope, fieldKey);
  }

  getVisibleCountForScope(scope: string): number {
    return (this.normalFieldsByScope[scope] ?? []).filter(f => f.visible !== false).length;
  }

  getModifiedCountForScope(scope: string): number {
    let count = 0;
    this.dirtyFieldKeys.forEach(key => {
      const fields = this.mapFields[scope];
      if (fields?.some(f => this.dirtyKey(f.key, f.scope) === key)) count++;
    });
    return count;
  }

  get hasPendingChanges(): boolean { return this.dirtyFieldKeys.size > 0; }

  public resetDirtyState(): void {
    this.dirtyFieldKeys.clear();
  }

  toggleGroup(scope: string): void { this.expandedGroups[scope] = !this.expandedGroups[scope]; }
  isGroupExpanded(scope: string): boolean { return this.expandedGroups[scope] === true; }

  private saveScopeOrder(): void {
    try { localStorage.setItem('map-configuration20-scope-order', JSON.stringify(this.scopeOrder)); } catch (e) {}
  }

  private loadScopeOrder(): void {
    try {
      const saved = localStorage.getItem('map-configuration20-scope-order');
      if (saved) {
        const order: string[] = JSON.parse(saved);
        const valid = order.filter(s => Object.keys(this.mapFields).includes(s));
        const newScopes = Object.keys(this.mapFields).filter(s => !valid.includes(s));
        this.scopeOrder = [...valid, ...newScopes];
      } else {
        this.scopeOrder = Object.keys(this.mapFields).sort();
      }
    } catch (e) {
      this.scopeOrder = Object.keys(this.mapFields).sort();
    }
  }

  getScopeIcon(scope: string): any {
    const iconMap: { [k: string]: any } = {
      signal: this.faSignal, tcu: this.faCogs, platform: this.faDesktop,
      erp: this.faBuilding, common: this.faWrench, model: this.faCogs,
      other: this.faQuestionCircle, general: this.faQuestionCircle
    };
    return iconMap[scope] || this.faQuestionCircle;
  }

  getScopeLabel(scope: string): string {
    return this.catalogService.getScopeLabel(scope);
  }

  hasAvailableModels(field: MergedField): boolean { return (field.availableInModels?.length ?? 0) > 0; }

  formatModelsTooltip(field: MergedField): string {
    const models = field.availableInModels;
    if (!models?.length) return '';
    const header = this.translateService.instant('confSignalsPanel.modelsTooltipHeader');
    const list = models.map((m, i) => `${i + 1}. ${m}`).join('\n');
    const footer = this.translateService.instant('confSignalsPanel.modelsTooltipFooter');
    return `${header}\n${'━'.repeat(25)}\n\n${list}\n\n${footer}`;
  }

  getTotalFieldsCount(): number {
    return Object.values(this.mapFields).reduce((t, f) => t + f.length, 0);
  }

  trackByScope(_i: number, scope: string): string { return scope; }
  trackByFieldKey(_i: number, field: MergedField): string { return field.key; }

  loadMappingConfiguration(): void { this.buildMergedFields(); }

  public reloadData(): void { this.buildMergedFields(); }
}
